import React, { useEffect } from 'react';
import { X, Package, Layers, Filter, CheckCircle, Award, Ruler, Settings, Wrench } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../i18n/LanguageContext';
import { Link } from 'react-router-dom';

const ProductDetailModal = ({ product, onClose }) => {
  const { language } = useLanguage();

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (!product) return null;

  const productName = language === 'id' ? product.nameId : product.name;
  const productDesc = language === 'id' ? product.descriptionId : product.description;
  const hasSubItems = product.subItems && product.subItems.length > 0;
  const hasFilters = product.filters && Object.keys(product.filters).length > 0;
  const hasBrands = product.brands && product.brands.length > 0;
  const hasSizes = product.sizes && product.sizes.length > 0;
  const hasPressureRating = product.pressureRating && product.pressureRating.length > 0;
  const hasApplications = product.applications && product.applications.length > 0;
  const hasImage = product.image;

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={onClose}
    >
      {/* Mobile: Full screen scrollable | Desktop: Centered modal */}
      <div className="h-full w-full overflow-y-auto">
        <div className="min-h-full flex items-start lg:items-center justify-center p-0 sm:p-4">
          <div 
            className="bg-white w-full sm:rounded-2xl sm:max-w-5xl min-h-screen sm:min-h-0 sm:my-4 lg:my-0 shadow-2xl animate-in zoom-in-95 duration-300 flex flex-col lg:flex-row lg:max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Left Side - Image (sticky on desktop) */}
            {hasImage && (
              <div className="relative lg:w-2/5 h-36 sm:h-48 lg:h-auto flex-shrink-0 overflow-hidden lg:sticky lg:top-0">
                <img
                  src={product.image}
                  alt={productName}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
                
                {/* Product name overlay on image */}
                <div className="absolute bottom-3 left-3 right-12 sm:bottom-4 sm:left-4 sm:right-4">
                  <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold text-white">{productName}</h2>
                </div>
                
                {/* Close button on image (mobile) */}
                <button
                  onClick={onClose}
                  className="lg:hidden absolute top-3 right-3 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg"
                >
                  <X size={18} className="text-gray-700" />
                </button>
              </div>
            )}

            {/* Right Side - Content */}
            <div className="relative flex-1 flex flex-col lg:overflow-hidden">
              {/* Close Button (desktop) */}
              <button
                onClick={onClose}
                className="hidden lg:flex absolute top-4 right-4 z-10 w-10 h-10 bg-gray-100 hover:bg-red-100 rounded-full items-center justify-center transition-all hover:scale-110 shadow-lg group"
              >
                <X size={20} className="text-gray-700 group-hover:text-[#CF2E2E] transition-colors" />
              </button>

              {/* Close button when no image (mobile) */}
              {!hasImage && (
                <button
                  onClick={onClose}
                  className="lg:hidden absolute top-3 right-3 w-8 h-8 bg-gray-100 hover:bg-red-100 rounded-full flex items-center justify-center shadow-lg"
                >
                  <X size={18} className="text-gray-700" />
                </button>
              )}

              {/* Header (when no image) */}
              {!hasImage && (
                <div className="p-4 sm:p-6 pb-0 pr-14">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{productName}</h2>
                  <p className="text-gray-600 text-sm sm:text-base">{productDesc}</p>
                </div>
              )}

              {/* Scrollable Content Area */}
              <div className="flex-1 lg:overflow-y-auto p-4 sm:p-6 space-y-4">
                {/* Description (mobile only when has image) */}
                {hasImage && (
                  <p className="text-gray-600 text-sm sm:text-base lg:hidden">{productDesc}</p>
                )}
                
                {/* Description (desktop only) */}
                {hasImage && (
                  <p className="text-gray-600 text-sm sm:text-base hidden lg:block">{productDesc}</p>
                )}

                {/* Brands Section */}
                {hasBrands && (
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100/30 p-4 rounded-xl border-l-4 border-[#0693E3]">
                    <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                      <Award className="mr-2 text-[#0693E3]" size={18} />
                      {language === 'id' ? 'Merek Tersedia' : 'Available Brands'}
                    </h4>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {product.brands.map((brand, idx) => (
                        <span
                          key={idx}
                          className="px-2 sm:px-3 py-1 sm:py-1.5 bg-white border-2 border-[#0693E3] text-[#0693E3] rounded-lg text-xs sm:text-sm font-semibold"
                        >
                          {brand}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sizes Section */}
                {hasSizes && (
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 p-4 rounded-xl border-l-4 border-gray-400">
                    <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                      <Ruler className="mr-2 text-gray-600" size={18} />
                      {language === 'id' ? 'Ukuran Tersedia' : 'Available Sizes'}
                    </h4>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {product.sizes.slice(0, 12).map((size, idx) => (
                        <span
                          key={idx}
                          className="px-2 sm:px-3 py-1 sm:py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-xs sm:text-sm"
                        >
                          {size}
                        </span>
                      ))}
                      {product.sizes.length > 12 && (
                        <span className="px-2 sm:px-3 py-1 sm:py-1.5 bg-gray-200 text-gray-600 rounded-lg text-xs sm:text-sm font-medium">
                          +{product.sizes.length - 12} {language === 'id' ? 'lainnya' : 'more'}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Pressure Rating Section */}
                {hasPressureRating && (
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100/30 p-4 rounded-xl border-l-4 border-orange-400">
                    <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                      <Settings className="mr-2 text-orange-500" size={18} />
                      {language === 'id' ? 'Rating Tekanan' : 'Pressure Rating'}
                    </h4>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {product.pressureRating.map((rating, idx) => (
                        <span
                          key={idx}
                          className="px-2 sm:px-3 py-1 sm:py-1.5 bg-white border border-orange-300 text-orange-700 rounded-lg text-xs sm:text-sm font-medium"
                        >
                          {rating}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sub-items / Types */}
                {hasSubItems && (
                  <div className="bg-gradient-to-br from-green-50 to-green-100/30 p-4 rounded-xl border-l-4 border-green-500">
                    <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                      <Layers className="mr-2 text-green-600" size={18} />
                      {language === 'id' ? 'Jenis / Tipe Tersedia' : 'Available Types'}
                    </h4>
                    <div className="grid gap-1.5 sm:gap-2">
                      {product.subItems.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center px-3 py-2 bg-white border border-green-200 rounded-lg"
                        >
                          <CheckCircle className="text-green-500 mr-2 flex-shrink-0" size={14} />
                          <span className="font-medium text-gray-800 text-xs sm:text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Specifications / Filters */}
                {hasFilters && (
                  <div className="bg-gradient-to-br from-red-50 to-red-100/30 p-4 rounded-xl border-l-4 border-[#CF2E2E]">
                    <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                      <Filter className="mr-2 text-[#CF2E2E]" size={18} />
                      {language === 'id' ? 'Spesifikasi Tersedia' : 'Available Specifications'}
                    </h4>
                    <div className="space-y-3">
                      {Object.entries(product.filters).map(([filterKey, filterValues]) => (
                        <div key={filterKey}>
                          <p className="text-xs font-semibold text-gray-600 uppercase mb-1.5">
                            {filterKey.replace(/_/g, ' ')}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {filterValues.slice(0, 8).map((value, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-white border border-red-200 text-gray-700 rounded-lg text-xs"
                              >
                                {value}
                              </span>
                            ))}
                            {filterValues.length > 8 && (
                              <span className="px-2 py-1 bg-red-100 text-[#CF2E2E] rounded-lg text-xs font-medium">
                                +{filterValues.length - 8}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Applications Section */}
                {hasApplications && (
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100/30 p-4 rounded-xl border-l-4 border-purple-500">
                    <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                      <Wrench className="mr-2 text-purple-600" size={18} />
                      {language === 'id' ? 'Aplikasi' : 'Applications'}
                    </h4>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {product.applications.map((app, idx) => (
                        <span
                          key={idx}
                          className="px-2 sm:px-3 py-1 sm:py-1.5 bg-white border border-purple-300 text-purple-700 rounded-lg text-xs sm:text-sm font-medium"
                        >
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* If no detailed info, show generic message */}
                {!hasSubItems && !hasFilters && !hasBrands && !hasSizes && (
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100/30 p-4 rounded-xl border-l-4 border-gray-400">
                    <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                      <Package className="mr-2 text-gray-600" size={18} />
                      {language === 'id' ? 'Informasi Produk' : 'Product Information'}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {language === 'id' 
                        ? 'Hubungi tim kami untuk informasi lebih lanjut mengenai produk ini, termasuk spesifikasi teknis, ketersediaan, dan harga.'
                        : 'Contact our team for more information about this product, including technical specifications, availability, and pricing.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons - Sticky at bottom */}
              <div className="sticky bottom-0 flex-shrink-0 p-4 sm:p-6 bg-gray-50 border-t border-gray-200">
                <div className="flex gap-2 sm:gap-3">
                  <Link to="/contact" className="flex-1">
                    <Button 
                      className="w-full bg-[#CF2E2E] hover:bg-[#B02525] text-white font-semibold shadow-lg transition-all py-2.5 sm:py-4 text-sm sm:text-base"
                      onClick={onClose}
                    >
                      {language === 'id' ? 'Minta Penawaran' : 'Request Quotation'}
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    className="flex-1 border-2 border-gray-300 hover:bg-gray-100 font-semibold py-2.5 sm:py-4 text-sm sm:text-base"
                    onClick={onClose}
                  >
                    {language === 'id' ? 'Tutup' : 'Close'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;
